import { useCallback, useEffect, useState } from 'react'
import { Icon, PageHeader } from '@carrent/ui'
import { kbApi } from '@carrent/api'
import { UserPicker } from '../../tasks/UserPicker'

/**
 * Группы пользователей.
 *
 * Две природы в одном списке, и это видно с первого взгляда.
 *
 * Региональные группы автоматические: состав считается из регионов
 * водителей, менять его здесь нельзя — иначе появился бы второй
 * источник правды, который разъедется с первым. Их строки помечены
 * и не имеют кнопок правки; чтобы изменить состав, водителя переводят
 * между регионами на его карточке.
 *
 * Пользовательские группы ведут руками: «Наставники», «Пилот нового
 * приложения» — всё, что не выводится из регионов.
 */
export default function GroupsPage() {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const [openGroup, setOpenGroup] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    kbApi.groups()
      .then((r) => setGroups(r.data?.data || []))
      .catch((e) => setError(e.response?.data?.message || 'Не удалось загрузить группы'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const custom = groups.filter((g) => g.kind === 'custom')
  const regional = groups.filter((g) => g.kind === 'region')

  return (
    <div className="groups">
      <PageHeader
        title="Группы"
        right={
          <button type="button" className="btn btn--primary btn--sm"
            onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} aria-hidden="true" />
            Создать группу
          </button>
        }
      />

      <p className="groups__lede">
        Группам выдают доступ к пространствам базы знаний. Региональные группы
        собираются сами из регионов водителей — их состав меняется на карточке
        пользователя.
      </p>

      {loading && <div className="groups__loading"><div className="spinner" /></div>}
      {error && <p className="kb__error">{error}</p>}

      {!loading && !error && (
        <>
          <GroupSection
            title="Пользовательские"
            hint="Состав задаётся вручную"
            groups={custom}
            emptyText="Пока ни одной. Создайте первую — например, «Наставники»."
            onOpen={setOpenGroup}
            onChanged={load}
          />
          <GroupSection
            title="Региональные"
            hint="Собираются автоматически"
            groups={regional}
            emptyText="Появятся вместе с регионами."
            onOpen={setOpenGroup}
            onChanged={load}
          />
        </>
      )}

      {creating && (
        <CreateGroupModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load() }}
        />
      )}
      {openGroup && (
        <GroupMembersModal
          group={openGroup}
          onClose={() => setOpenGroup(null)}
          onChanged={load}
        />
      )}
    </div>
  )
}

function GroupSection({ title, hint, groups, emptyText, onOpen, onChanged }) {
  return (
    <section className="groups__section">
      <header className="groups__section-head">
        <h2 className="groups__section-title">{title}</h2>
        <span className="groups__section-hint">{hint}</span>
      </header>

      {groups.length === 0 ? (
        <p className="groups__empty">{emptyText}</p>
      ) : (
        <table className="groups__table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Участников</th>
              <th aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <GroupRow key={g.id} group={g} onOpen={onOpen} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function GroupRow({ group, onOpen, onChanged }) {
  const system = group.kind === 'region'

  const remove = async () => {
    if (!confirm(`Удалить группу «${group.name}»? Её доступы к пространствам будут сняты.`)) {
      return
    }
    try {
      await kbApi.deleteGroup(group.id)
      onChanged()
    } catch (e) {
      alert(e.response?.data?.message || 'Не удалось удалить группу')
    }
  }

  return (
    <tr>
      <td>
        <button type="button" className="groups__name" onClick={() => onOpen(group)}>
          {group.name}
        </button>
        {system && (
          <span className="groups__auto" title="Состав определяется регионами водителей">
            автоматическая
          </span>
        )}
        {group.description && (
          <span className="groups__descr">{group.description}</span>
        )}
      </td>
      <td className="groups__count">{group.member_count ?? '—'}</td>
      <td className="groups__actions">
        <button type="button" className="btn btn--ghost btn--sm"
          onClick={() => onOpen(group)}>
          {system ? 'Состав' : 'Участники'}
        </button>
        {!system && (
          <button type="button" className="btn btn--ghost btn--sm groups__danger"
            onClick={remove}>
            Удалить
          </button>
        )}
      </td>
    </tr>
  )
}

function CreateGroupModal({ onClose, onCreated }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    if (!name.trim()) {
      setError('Введите название')
      return
    }
    setSaving(true)
    setError('')
    try {
      await kbApi.createGroup({ name: name.trim(), description: description.trim() })
      onCreated()
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось создать группу')
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Создать группу">
      <div className="modal">
        <header className="modal__head">
          <h2 className="modal__title">Создать группу</h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="modal__body">
          <label className="field">
            <span className="field__label">Название</span>
            <input className="input" value={name} autoFocus
              placeholder="Наставники"
              onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Зачем эта группа</span>
            <input className="input" value={description}
              placeholder="Опытные водители, проверяют новичков"
              onChange={(e) => setDescription(e.target.value)} />
          </label>
          {error && <p className="field__error">{error}</p>}
        </div>

        <footer className="modal__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Отмена</button>
          <button type="button" className="btn btn--primary" onClick={submit} disabled={saving}>
            {saving ? 'Создаём…' : 'Создать'}
          </button>
        </footer>
      </div>
    </div>
  )
}

function GroupMembersModal({ group, onClose, onChanged }) {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const system = group.kind === 'region'

  const load = useCallback(() => {
    setLoading(true)
    kbApi.groupMembers(group.id)
      .then((r) => setMembers(r.data?.data?.members || []))
      .catch((e) => setError(e.response?.data?.message || 'Не удалось загрузить состав'))
      .finally(() => setLoading(false))
  }, [group.id])

  useEffect(() => { load() }, [load])

  const add = async (user) => {
    if (!user) return
    try {
      await kbApi.addGroupMember(group.id, user.id)
      load()
      onChanged()
    } catch (e) {
      alert(e.response?.data?.message || 'Не удалось добавить')
    }
  }

  const remove = async (userId) => {
    try {
      await kbApi.removeGroupMember(group.id, userId)
      load()
      onChanged()
    } catch (e) {
      alert(e.response?.data?.message || 'Не удалось удалить')
    }
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true"
      aria-label={`Состав группы ${group.name}`}>
      <div className="modal modal--wide">
        <header className="modal__head">
          <h2 className="modal__title">{group.name}</h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="modal__body">
          {system ? (
            <p className="groups__note">
              Это автоматическая группа: в неё входят водители региона.
              Чтобы изменить состав, поменяйте регионы водителя на его карточке.
            </p>
          ) : (
            <div className="groups__add">
              <span className="field__label">Добавить участника</span>
              <UserPicker selected={null} onChange={add} placeholder="Имя или телефон" />
            </div>
          )}

          {loading && <div className="groups__loading"><div className="spinner" /></div>}
          {error && <p className="field__error">{error}</p>}

          {!loading && members.length === 0 && (
            <p className="groups__empty">
              {system ? 'В регионе пока нет водителей.' : 'Участников пока нет.'}
            </p>
          )}

          {members.length > 0 && (
            <ul className="groups__members">
              {members.map((m) => (
                <li key={m.id} className="groups__member">
                  <span className="groups__member-name">{m.full_name}</span>
                  <span className="groups__member-phone">{m.phone}</span>
                  {!system && (
                    <button type="button" className="btn btn--ghost btn--sm groups__danger"
                      onClick={() => remove(m.id)}>
                      Убрать
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
